import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
  debounce,
  updateCollectionGridFit,
  yieldToMainThread,
} from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/** Slideshows to update per yielded chunk once the in-viewport ones are done. */
const MEDIA_VIEW_CHUNK_SIZE = 4;

/**
 * Splits elements into those currently within the vertical viewport and the rest.
 * @param {Element[]} elements
 * @returns {[Element[], Element[]]}
 */
function partitionByViewport(elements) {
  const onscreen = [];
  const offscreen = [];

  for (const element of elements) {
    const { top, bottom } = element.getBoundingClientRect();
    (bottom > 0 && top < window.innerHeight ? onscreen : offscreen).push(element);
  }

  return [onscreen, offscreen];
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.classList.contains('product-grid-container--collection')) {
      window.addEventListener('resize', this.#handleCardGalleryResize);
    }

    if (this.refs.grid?.getAttribute('product-grid-view') === 'zoom-out' && !this.#hasMultiplePagesLoaded()) {
      this.renderNextPage();
    }

    // The server-rendered first page is subject to the same column mismatch as
    // every later one, so settle it before the shopper sees a ragged edge under
    // the load-more button.
    this.settleRows();
  }

  disconnectedCallback() {
    // PaginatedList's teardown disconnects the infinite-scroll observer and drops
    // the document-level FilterUpdate listener; without this the grid's observers
    // and that listener outlive every section re-render.
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('resize', this.#handleCardGalleryResize);
  }

  /**
   * Recalculates the collection grid's column-bump fit on resize, then re-settles
   * its rows against the new column count.
   */
  #handleCardGalleryResize = debounce(() => {
    updateCollectionGridFit();
    // A resize can add or drop a column, changing which items complete a row.
    this.settleRows();
  }, 150);

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Switches all product cards between model and product imagery.
   *
   * @param {Event} event
   */
  async updateProductMediaView(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-media-view]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    for (const viewButton of this.querySelectorAll('[data-media-view]')) {
      viewButton.setAttribute('aria-pressed', viewButton === button ? 'true' : 'false');
    }

    await this.#applyMediaView();
  }

  /**
   * Points every card slideshow that isn't already showing the selected view at it. The selected view lives in the toggle buttons' `aria-pressed`, so cards
   * that arrive later (Load more, infinite scroll, a filter/sort morph) are
   * brought in line by `settleRows()` calling this - they are server-rendered on
   * the model slide and know nothing about the client-side choice.
   */
  async #applyMediaView(attempt = 0, { prePaint = false } = {}) {
    const selectedView =
      this.querySelector('[data-media-view][aria-pressed="true"]')?.getAttribute('data-media-view') ?? 'model';
    const slideIndex = selectedView === 'product' ? 1 : 0;
    let unsettled = false;

    const selectSlideshow = async (slideshow) => {
      slideshow.removeAttribute('disabled');
      await slideshow.select?.(slideIndex, undefined, { animate: false });

      // `select()` silently does nothing until the slideshow has finished its
      // own setup (a card appended a moment ago), so check it landed and retry.
      if (slideshow.current !== slideIndex) unsettled = true;
    };

    // Cards `settleRows()` is holding back (`hidden`) are skipped: a slideshow
    // that is `display: none` loses its scroll position, so a slide picked now
    // would snap back once the card is revealed (`settleRows()` itself hides and
    // reveals trailing items while measuring, which is also why this checks the
    // slideshow's real `current` slide rather than remembering what was applied).
    const slideshows = [...this.querySelectorAll('.card-gallery slideshow-component')].filter(
      (slideshow) => !slideshow.closest('[hidden]') && slideshow.current !== slideIndex
    );

    // For cards that have just been added, move the scroller straight to the
    // selected slide in this same task, before the browser paints them. The
    // `select()` calls below are async and silently no-op until a new card's
    // slideshow has finished setting up, which let the model shot flash for a
    // moment first; this makes them appear on the right slide. Layout reads are
    // batched ahead of the writes. `select()` then only syncs the slideshow's
    // own state (current slide, aria, progress).
    if (prePaint) {
      // Judged by where the scroller really is, not by `current`: hiding and
      // re-showing a card (which `settleRows()` does while measuring) resets its
      // scroll position without `current` ever finding out.
      const jumps = [];
      for (const slideshow of this.querySelectorAll('.card-gallery slideshow-component')) {
        if (slideshow.closest('[hidden]')) continue;

        const scroller = slideshow.querySelector('[ref="scroller"]');
        const slide = slideshow.querySelectorAll('slideshow-slide')[slideIndex];
        if (!scroller || !slide) continue;

        const offset = slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
        if (Math.abs(offset) > 1) {
          slideshow.removeAttribute('disabled');
          jumps.push([scroller, scroller.scrollLeft + offset]);
        }
      }
      for (const [scroller, left] of jumps) scroller.scrollTo({ left, behavior: 'instant' });

      // The slideshow follows its scroller's own scroll event to update `current`
      // and the controls. Calling `select()` on top would scroll from that stale
      // `current` and undo the jump, so this path stops here.
      return;
    }

    const [onscreen, offscreen] = partitionByViewport(slideshows);

    await Promise.all(onscreen.map(selectSlideshow));

    // Offscreen cards aren't visible mid-toggle, so update them in yielded
    // chunks instead of one long synchronous pass over every card in the grid.
    for (let i = 0; i < offscreen.length; i += MEDIA_VIEW_CHUNK_SIZE) {
      await yieldToMainThread();
      await Promise.all(offscreen.slice(i, i + MEDIA_VIEW_CHUNK_SIZE).map(selectSlideshow));
    }

    if (unsettled && attempt < 5) {
      setTimeout(() => this.isConnected && this.#applyMediaView(attempt + 1), 250);
    }
  }

  /**
   * Loads the next page when manual pagination is enabled.
   *
   * @param {Event} event
   */
  async loadNextPage(event) {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    // Let the browser handle modified clicks (new tab, new window, download) as a plain link.
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    // An anchor has no `disabled`: ignore clicks while a page is already on its way.
    if (link.getAttribute('aria-disabled') === 'true') return;

    link.setAttribute('aria-disabled', 'true');
    await this.renderNextPage();

    // `renderNextPage()` re-settles the grid, and this class's `settleRows()`
    // override already refreshed the link's label and `href` - and removed it, if
    // nothing was left - from there. Nothing further to compute here.
    if (link.isConnected) {
      link.removeAttribute('aria-disabled');
    }
  }

  /**
   * Trims the grid's last row, then keeps the "Load more (N)" button's count
   * in sync with it.
   *
   * @param {{ floor?: number }} [options]
   */
  settleRows(options) {
    super.settleRows(options);
    this.#updateLoadMoreLabel();
    this.#syncCardImageSizes([this.refs.grid]);
    this.#applyMediaView(0, { prePaint: true });
  }

  /**
   * Sizes a fetched page's images before insertion starts their fetch.
   *
   * @param {NodeListOf<Element>} items
   */
  prepareItems(items) {
    this.#syncCardImageSizes(items);
  }

  /**
   * Sizes unloaded card images to the dense view's tile width (the server's `sizes`
   * assumes the default column count), and restores the server value on leaving it.
   *
   * @param {Iterable<Element | undefined>} roots
   */
  #syncCardImageSizes(roots) {
    const { grid } = this.refs;
    if (!grid) return;

    const dense = grid.getAttribute('product-grid-view') === 'zoom-out';
    const gallery = dense ? grid.querySelector(':scope > :not([hidden]) .card-gallery') : null;
    const width = gallery ? Math.ceil(gallery.getBoundingClientRect().width) : 0;
    const images = [...roots].flatMap((root) => (root ? [...root.querySelectorAll('.card-gallery img[sizes]')] : []));

    for (const img of images) {
      if (!(img instanceof HTMLImageElement)) continue;

      if (dense) {
        // Setting `sizes` restarts an in-flight load even when the value is unchanged, and
        // this runs over the whole grid on every settle, so only write a real change.
        const sizes = `${width}px`;
        if (!width || img.sizes === sizes || (img.complete && img.naturalWidth)) continue;
        img.dataset.defaultSizes ??= img.sizes;
        img.sizes = sizes;
      } else if (img.dataset.defaultSizes) {
        img.sizes = img.dataset.defaultSizes;
        delete img.dataset.defaultSizes;
      }
    }
  }

  /**
   * Recomputes the "Load more" button's remaining-item count against what's
   * actually visible, since the server-rendered count only knows about
   * pagination - not the rows `settleRows()` may have since held back. Left
   * uncorrected, a click reveals more products than the button promised (a
   * settle that hides 4 items promises 3 but loads 7).
   *
   * `sections/main-collection.liquid`'s `settleGridRowsEarly()` mirrors this
   * so the count is already correct at first paint, before this module has
   * loaded - see there. Keep both in sync.
   */
  #updateLoadMoreLabel() {
    const { grid, viewMoreNext } = this.refs;

    if (!grid || !(viewMoreNext instanceof HTMLAnchorElement)) return;

    const totalItems = Number(viewMoreNext.dataset.totalItems);
    // Promo tiles aren't products, so `data-product-id` excludes them - same
    // selector `loadNextPage()` used to use directly.
    const visibleItems = grid.querySelectorAll(':scope > [data-product-id]:not([hidden])').length;
    // A `?page=N` document starts at product (N - 1) x page size: those earlier
    // products were never rendered here, but the shopper is not owed them again.
    const firstCard = grid.querySelector(':scope > [ref="cards[]"]');
    const skippedItems = firstCard instanceof HTMLElement ? (Number(firstCard.dataset.page) - 1) * Number(viewMoreNext.dataset.pageSize) : 0;
    const remainingItems = Math.max(totalItems - (skippedItems || 0) - visibleItems, 0);
    const label = viewMoreNext.querySelector('[data-load-more-label]');

    if (label) {
      label.textContent = `${viewMoreNext.dataset.label} (${remainingItems})`;
    }

    if (remainingItems === 0) {
      viewMoreNext.closest('.product-grid__load-more')?.remove();
      return;
    }

    // Point the link at the page after the last one on screen, so it stays a
    // working link (and what a crawler sees) as pages are appended. The URL is
    // built from the document's own, as `#getPage('next')` does, so filters and
    // sort carry over.
    // Read from the DOM, not `refs.cards`: those refs are refreshed after this runs.
    const pageCards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastCard = pageCards[pageCards.length - 1];
    const nextPage = lastCard instanceof HTMLElement ? Number(lastCard.dataset.page) + 1 : NaN;

    if (nextPage && nextPage <= Number(grid.dataset.lastPage)) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());
      url.hash = '';
      viewMoreNext.href = url.pathname + url.search;
    }
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Uses its own transition type (not 'product-grid', which `facets.js` uses for
    // filter changes) so it skips that type's per-card JS handling in
    // `utilities.js` — a full section replace needs per-card `view-transition-name`s
    // to morph identity across brand-new elements, but this toggle just resizes the
    // same elements via CSS, so the shared grid-level animation is enough. See
    // `base.css`'s `:active-view-transition-type(product-grid, product-grid-density)`.
    await startViewTransition(() => this.#setLayout(value), ['product-grid-density']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });

    // The dense 7-across view fits more products per row, so a single page
    // of products fills fewer rows than in the default view. Load the next
    // page so switching to it doesn't feel sparse, but only if we haven't
    // already loaded extra pages (e.g. via scrolling or a prior toggle) —
    // otherwise every toggle would keep appending more products. Deferred to
    // idle time so its DOM append and `settleRows()` reflow don't land in the
    // same frame as the transition that just finished.
    if (value === 'zoom-out' && !this.#hasMultiplePagesLoaded()) {
      requestIdleCallback(() => this.renderNextPage());
    }
  };

  /**
   * Whether more than one page of products is already rendered in the grid.
   *
   * @returns {boolean}
   */
  #hasMultiplePagesLoaded() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return false;

    const firstPage = cards[0]?.dataset.page;
    const lastPage = cards[cards.length - 1]?.dataset.page;

    return firstPage !== lastPage;
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    updateCollectionGridFit();
    // Switching density changes the column count (4 <-> 7), so what was a flush
    // row no longer is.
    this.settleRows();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
